'use client';
import { type ChangeEvent, useContext, useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import { CloseIcon, Icon } from 'components/UI/Icon';
import type { IconNames } from 'components/UI/Icon/Icons';
import { AppContext } from 'context/AppContext';
import { useConsentReady } from 'hooks/useConsentReady';
import {
  trackSatisfactionSurveyLayerClosed,
  trackSatisfactionSurveyLayerShown,
  trackSatisfactionSurveySubmitted,
} from 'utils/analytics';
import { isMobile } from 'utils/detect';
import { getOrCreateUserId, getVoted, setVoted } from 'utils/localStorage';
import styles from './default.module.scss';

export type WidgetSatisfactionSurveyProps = {
  readonly model?: {
    readonly fields?: {
      readonly intro?: string;
      readonly explanation?: string;
      readonly cta?: string;
      readonly feedback?: string;
    };
  };
};

type RatingProps = {
  onClose: () => void;
  onStepChange: () => void;
  intro?: string;
  expandedData?: ExpandedData;
};

type ReasoningProps = {
  onClose: () => void;
  onStepChange: () => void;
  explanation?: string;
  cta?: string;
  expandedData?: ExpandedData;
};

type FeedbackProps = {
  onClose: () => void;
  feedback?: string;
};

type ExpandedData = {
  viewport: string;
  pageType: string;
  section: string;
  os: string;
  trafficSource: string;
};

const collectExpandedData = (
  pageTitle: string,
  homeSectionPath: string,
  device: string,
  os: string,
  trafficSource: string
): ExpandedData => {
  return {
    viewport: device,
    pageType: pageTitle === 'frontpage' ? 'home' : 'section page',
    section: homeSectionPath.split('/')[0],
    os,
    trafficSource,
  };
};

const Rating = ({ onClose, onStepChange, intro, expandedData }: RatingProps) => {
  const ratingOptions = Array.from({ length: 5 }, (_, i) => i + 1);
  const ratingLabels = ['sehr unzufrieden', 'unzufrieden', 'neutral', 'zufrieden', 'sehr zufrieden'];

  const handleRating = async (i: number) => {
    setVoted(true);
    trackSatisfactionSurveySubmitted(1, i);
    onStepChange();

    const fields = JSON.stringify({ Rating: i, UserID: getOrCreateUserId(), Date: new Date(), ...expandedData });

    await fetch('/nextapi/powerbi-push', {
      method: 'POST',
      body: fields,
      cache: 'no-store',
    });
  };

  return (
    <>
      <div className={styles.intro}>
        <b>{intro ?? 'Wie zufrieden oder unzufrieden sind Sie heute mit unserer Website?'}</b>
        <CloseIcon onClose={onClose} label="Zufriedenheitsabfrage schließen" />
      </div>
      <div className={styles.rating}>
        {ratingOptions.map((i) => (
          <button
            key={`satisfaction${i}`}
            aria-label={`Zufriedenheitsrating: ${ratingLabels[i - 1]}`}
            onClick={() => handleRating(i)}
          >
            <Icon name={`satisfaction${i}` as IconNames} size={32} />
          </button>
        ))}
      </div>
      <div className={styles.labels}>
        <span>
          Sehr <br />
          unzufrieden
        </span>
        <span>
          Sehr <br />
          zufrieden
        </span>
      </div>
    </>
  );
};

const Reasoning = ({ onClose, onStepChange, explanation, cta, expandedData }: ReasoningProps) => {
  const [inputCount, setInputCount] = useState<number>(0);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setInputCount(e.target.value.length);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    trackSatisfactionSurveySubmitted(2);
    onStepChange();

    const fields = JSON.stringify({
      Explination: e.currentTarget.reason.value,
      userID: getOrCreateUserId(),
      Date: new Date(),
      ...expandedData,
    });

    await fetch('/nextapi/powerbi-push', {
      method: 'POST',
      body: fields,
      cache: 'no-store',
    });
  };

  return (
    <>
      <div className={styles.intro}>
        <b>{explanation ?? 'Was ist der Hauptgrund für Ihre Bewertung? (optional)'}</b>
        <CloseIcon onClose={onClose} label="Zufriedenheitsabfrage schließen" />
      </div>
      <form onSubmit={handleSubmit}>
        <div className={styles.wrapper}>
          <textarea
            name="reason"
            aria-label="Zufriedenheitsabfrage Begründung"
            placeholder="Mir gefällt / gefällt nicht, dass..."
            className={styles.textarea}
            maxLength={500}
            onChange={handleChange}
          />
          <span className={styles.counter}>{inputCount} / 500 </span>
        </div>

        <button className={styles.cta} type="submit" aria-label="Feedback senden">
          <Icon name="paperPlane" size={18} />
          <b>{cta ?? 'Feedback senden'}</b>
        </button>
      </form>
    </>
  );
};

const Feedback = ({ onClose, feedback }: FeedbackProps) => (
  <div className={styles.intro}>
    <b>{feedback ?? 'Feedback wurde gesendet.'}</b>
    <CloseIcon onClose={() => onClose} label="Zufriedenheitsabfrage schließen" />
  </div>
);

export const WidgetSatisfactionSurvey = ({ context }: { context: WidgetSatisfactionSurveyProps }) => {
  const { intro, explanation, cta, feedback } = context.model?.fields || {};
  const consentReady = useConsentReady();
  const {
    pageTitle,
    analyticsData: { homeSectionPath },
    device,
    os,
    trafficSource,
  } = useContext(AppContext);

  const expandedData = collectExpandedData(
    pageTitle ?? 'homepage',
    homeSectionPath ?? 'home',
    device ?? 'mobile',
    os ?? 'android',
    trafficSource ?? 'direct'
  );

  const [showSurvey, setShowSurvey] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState<number>(1);

  useEffect(() => {
    if (isMobile() && consentReady && !getVoted()) {
      const timer = setTimeout(() => {
        setShowSurvey(true);
        trackSatisfactionSurveyLayerShown();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [consentReady]);

  const handleVote = () => {
    setActiveStep(2);
  };

  const handleClose = () => {
    setShowSurvey(false);
    trackSatisfactionSurveyLayerClosed(activeStep);
  };

  const handleFeedback = () => {
    setActiveStep(3);
    const timer = setTimeout(() => {
      setShowSurvey(false);
    }, 3000);
    return () => clearTimeout(timer);
  };

  return (
    <AnimatePresence>
      {showSurvey && (
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className={clsx(styles.card, { [styles.surveyClosed]: !showSurvey })}
        >
          {activeStep === 1 && (
            <Rating onClose={handleClose} onStepChange={handleVote} intro={intro} expandedData={expandedData} />
          )}
          {activeStep === 2 && (
            <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
              <Reasoning
                onClose={handleClose}
                onStepChange={handleFeedback}
                explanation={explanation}
                cta={cta}
                expandedData={expandedData}
              />
            </motion.div>
          )}
          {activeStep === 3 && (
            <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
              <Feedback onClose={handleClose} feedback={feedback} />
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
